// Aba Pix do checkout (mockup, tela 2). Antes de gerar: dica + botão
// "Gerar Pix · R$ X". Depois: QR, contador até `expiresAt`, copia-e-cola e
// o aviso de espera — a confirmação chega pelo socket (quem escuta é o
// index). Portado de CheckoutPage/CheckoutSuccess, agora no formato
// `{ qrCode, qrCodeBase64, expiresAt }` do POST /subscriptions.
import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import TimerIcon from "@material-ui/icons/Timer";
import CropFreeIcon from "@material-ui/icons/CropFree";

import { i18n } from "../../translate/i18n";
import { AEROS, stateText } from "../../styles/aerosTokens";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    wrap: { display: "flex", flexDirection: "column", gap: 14, alignItems: "center" },
    hint: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      textAlign: "center",
      lineHeight: 1.5,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 10,
      padding: "12px 0",
      "& .MuiSvgIcon-root": { fontSize: 40, color: theme.palette.aeros.textTertiary },
    },
    qrBox: {
      width: 196,
      height: 196,
      borderRadius: 14,
      backgroundColor: "#F1F2F6",
      padding: 12,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& img": { width: "100%", height: "100%" },
    },
    timer: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: stateText(theme, AEROS.warning, "warning"),
      "& b": { fontVariantNumeric: "tabular-nums" },
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    timerExpired: { color: stateText(theme, AEROS.error, "error") },
    codeRow: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 10,
      padding: "0 4px 0 12px",
      boxSizing: "border-box",
    },
    code: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontFamily: "ui-monospace, monospace",
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    copyButton: {
      minHeight: 44,
      minWidth: 44,
      display: "flex",
      alignItems: "center",
      gap: 4,
      fontWeight: 700,
      fontSize: 12,
      color: stateText(theme, AEROS.cyan, "info"),
      background: "none",
      border: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      padding: "0 8px",
      flexShrink: 0,
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    copied: { color: stateText(theme, AEROS.success, "success") },
    waiting: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: theme.palette.text.secondary,
      backgroundColor: alpha(AEROS.cyan, 0.06),
      border: `1px solid ${alpha(AEROS.cyan, 0.2)}`,
      borderRadius: 10,
      padding: "9px 12px",
      width: "100%",
      boxSizing: "border-box",
      "& .MuiSvgIcon-root": {
        fontSize: 16,
        color: stateText(theme, AEROS.cyan, "info"),
        flexShrink: 0,
      },
    },
    fine: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: 2,
    },
    link: {
      minHeight: 44,
      background: "none",
      border: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 11.5,
      fontWeight: 600,
      color: stateText(theme, AEROS.cyan, "info"),
      padding: "0 6px",
    },
  };
});

const mmss = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const PixPanel = ({
  pix,
  submitLabel,
  submitClassName,
  generating,
  onGenerate,
  alreadyPaid,
  disabled,
}) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);

  const expiresAt = pix?.expiresAt ? new Date(pix.expiresAt).getTime() : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt || !Number.isFinite(expiresAt)) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const secondsLeft =
    expiresAt && Number.isFinite(expiresAt) ? (expiresAt - now) / 1000 : null;
  const expired = secondsLeft !== null && secondsLeft <= 0;

  const generateButton = (
    <Button
      variant="contained"
      color="primary"
      fullWidth
      className={submitClassName}
      onClick={onGenerate}
      disabled={generating || disabled}
      startIcon={generating ? <CircularProgress size={16} color="inherit" /> : null}
    >
      {submitLabel}
    </Button>
  );

  if (!pix?.qrCode || expired) {
    return (
      <div className={classes.wrap}>
        {expired ? (
          <div className={`${classes.timer} ${classes.timerExpired}`} role="status">
            <TimerIcon />
            {i18n.t("invoice.pay.expired")}
          </div>
        ) : (
          <div className={classes.hint}>
            <CropFreeIcon aria-hidden="true" />
            {i18n.t("subscription.checkout.pixHint")}
          </div>
        )}
        {generateButton}
      </div>
    );
  }

  const handleCopy = () => {
    setCopied(true);
    toast.success(i18n.t("invoice.pay.codeCopied"));
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.qrBox}>
        {pix.qrCodeBase64 ? (
          <img
            src={`data:image/png;base64,${pix.qrCodeBase64}`}
            alt={i18n.t("subscription.checkout.qrAlt")}
          />
        ) : (
          <QRCode
            value={pix.qrCode}
            size={172}
            title={i18n.t("subscription.checkout.qrAlt")}
          />
        )}
      </div>

      {secondsLeft !== null && (
        <div className={classes.timer}>
          <TimerIcon />
          {i18n.t("invoice.pay.validFor")} <b>{mmss(secondsLeft)}</b>
        </div>
      )}

      <div className={classes.codeRow}>
        <span className={classes.code} title={pix.qrCode}>
          {pix.qrCode}
        </span>
        <CopyToClipboard text={pix.qrCode} onCopy={handleCopy}>
          <button
            type="button"
            className={`${classes.copyButton} ${copied ? classes.copied : ""}`}
          >
            {copied ? <CheckCircleOutlineIcon /> : <FileCopyOutlinedIcon />}
            {i18n.t("invoice.pay.copyCode")}
          </button>
        </CopyToClipboard>
      </div>

      <div className={classes.waiting} role="status" aria-live="polite">
        <HourglassEmptyIcon />
        {i18n.t("subscription.checkout.waitingPix")}
      </div>

      {alreadyPaid}
    </div>
  );
};

// "Pagou e nada mudou? Já paguei" — também usado nos estados de espera do
// cartão (em análise / recorrente aguardando o webhook).
export const AlreadyPaid = ({ onClick, checking }) => {
  const classes = useStyles();
  return (
    <div className={classes.fine}>
      {i18n.t("subscription.checkout.alreadyPaidQuestion")}
      <button
        type="button"
        className={classes.link}
        onClick={onClick}
        disabled={checking}
      >
        {i18n.t("subscription.checkout.alreadyPaid")}
      </button>
    </div>
  );
};

export default PixPanel;
